import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Switch, message, Spin, Select, Tag } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { api } from '../../contexts/AuthContext';

const { Option } = Select;

const UserManagement = ({ isSeniorAdmin }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [pointsModalVisible, setPointsModalVisible] = useState(false);
  const [pointsForm] = Form.useForm();
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordForm] = Form.useForm();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users');
      setUsers(response.data.users);
    } catch (error) {
      message.error('获取用户列表失败');
      console.error('获取用户列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      email: user.email,
      nickname: user.nickname,
      is_active: user.is_active,
      phone: user.phone,
      points: user.points,
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingUser(null);
    form.resetFields();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        nickname: values.nickname,
        phone: values.phone,
        is_active: values.is_active,
      };
      await api.put(`/admin/users/${editingUser.id}`, data);
      message.success('用户信息更新成功');
      setIsModalVisible(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      message.error('更新用户信息失败');
      console.error('更新用户信息失败:', error);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      message.success('用户删除成功');
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.error || '删除用户失败');
      console.error('删除用户失败:', error);
    }
  };

  const handleOpenPointsModal = (user) => {
    setEditingUser(user);
    pointsForm.setFieldsValue({ points: user.points });
    setPointsModalVisible(true);
  };

  const handleSavePoints = async () => {
    try {
      const values = await pointsForm.validateFields();
      await api.put(`/admin/users/${editingUser.id}/points`, values);
      message.success('积分更新成功');
      setPointsModalVisible(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      message.error('更新积分失败');
      console.error('更新积分失败:', error);
    }
  };

  const handleOpenPasswordModal = (user) => {
    setEditingUser(user);
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  const handleSavePassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      await api.put(`/admin/users/${editingUser.id}/password`, { password: values.password });
      message.success('密码修改成功');
      setPasswordModalVisible(false);
      setEditingUser(null);
    } catch (error) {
      message.error(error.response?.data?.error || '修改密码失败');
      console.error('修改密码失败:', error);
    }
  };

  const renderRole = (user) => {
    if (user.role === 'admin') {
      const levels = {
        'super': '超级管理员',
        'senior': '高级管理员',
        'regular': '普通管理员',
      };
      return <Tag color="red">{levels[user.admin_level] || '管理员'}</Tag>;
    }
    if (user.role === 'publisher') {
      return <Tag color="blue">发布者</Tag>;
    }
    return <Tag color="green">创作者</Tag>;
  };

  const columns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
    },
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (_, record) => renderRole(record),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '活跃' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '积分',
      dataIndex: 'points',
      key: 'points',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {isSeniorAdmin && (
            <Button
              size="small"
              onClick={() => handleOpenPointsModal(record)}
            >
              修改积分
            </Button>
          )}
          {isSeniorAdmin && (
            <Button
              size="small"
              onClick={() => handleOpenPasswordModal(record)}
            >
              修改密码
            </Button>
          )}
          {isSeniorAdmin && (
            <Button
              type="danger"
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record.id)}
              disabled={record.role === 'admin' && record.admin_level === 'super'}
            >
              删除
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <h2>用户管理</h2>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px 0' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      )}

      <Modal
        title="编辑用户"
        open={isModalVisible}
        onOk={handleSave}
        onCancel={handleCancel}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="username"
            label="用户名"
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
          >
            <Input disabled />
          </Form.Item>
          <Form.Item name="nickname" label="昵称">
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="电话">
            <Input />
          </Form.Item>
          <Form.Item name="is_active" label="状态">
            <Switch checkedChildren="活跃" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="修改积分"
        open={pointsModalVisible}
        onOk={handleSavePoints}
        onCancel={() => {
          setPointsModalVisible(false);
          setEditingUser(null);
        }}
      >
        <Form form={pointsForm} layout="vertical">
          <Form.Item
            name="points"
            label="积分"
            rules={[{ required: true, message: '请输入积分值' }]}
          >
            <Input type="number" placeholder="请输入积分值" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="修改密码"
        open={passwordModalVisible}
        onOk={handleSavePassword}
        onCancel={() => {
          setPasswordModalVisible(false);
          setEditingUser(null);
        }}
      >
        <Form form={passwordForm} layout="vertical">
          <Form.Item
            name="password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码长度至少6位' },
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认密码"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入密码" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;